import { useMemo, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PlusCircle, Trash2, ChevronRight, ChevronLeft, CheckCircle2, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/lib/auth';

export default function WorkOrderManualPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canSeeMoney = ['owner', 'accounts', 'commercials'].includes(user.role);
  const canEnterMoney = ['owner', 'accounts'].includes(user.role);
  const STEPS = canSeeMoney
    ? ['Project', 'Reference', 'Location', 'Billing', 'Stages', 'Milestones', 'Values', 'Visits', 'Review']
    : ['Project', 'Reference', 'Location', 'Stages', 'Milestones', 'Visits', 'Review'];
  // Map effective step -> canonical step (with money screens preserved for money roles only)
  const stepMap = canSeeMoney
    ? [0, 1, 2, 3, 4, 5, 6, 7, 8]
    : [0, 1, 2, 4, 5, 7, 8];
  const [step, setStep] = useState(0);
  const [f, setF] = useState({
    project_name: '', client_name: '', client_wo_reference: '',
    location_address: '', location_city: '', location_state: '',
    billing_name: '', stage_plan: 'standard', custom_stages: [],
    milestones: [
      { name: 'Mobilisation', percentage: 20, trigger: 'on signing' },
      { name: 'Strategy Sign-off', percentage: 15, trigger: 'on approval' },
      { name: 'Concept Approval', percentage: 25, trigger: 'on approval' },
      { name: 'DD Submission', percentage: 25, trigger: 'on submission' },
      { name: 'TDP Handover', percentage: 15, trigger: 'on handover' },
    ],
    total_value_inr: '', included_visits: '', per_visit_fee_inr: '', visit_billing_mode: 'not_defined',
  });

  const totalPct = useMemo(() => Number((f.milestones.reduce((s, m) => s + Number(m.percentage || 0), 0)).toFixed(2)), [f.milestones]);
  const canSave = totalPct === 100 && f.project_name.trim() && f.client_name.trim();

  const setMs = (i, k, v) => setF(x => ({ ...x, milestones: x.milestones.map((m, j) => j === i ? { ...m, [k]: k === 'percentage' ? Number(v) : v } : m) }));
  const addMs = () => setF(x => ({ ...x, milestones: [...x.milestones, { name: '', percentage: 0, trigger: '' }] }));
  const rmMs = (i) => setF(x => ({ ...x, milestones: x.milestones.filter((_, j) => j !== i) }));

  const submit = async () => {
    try {
      const payload = { ...f,
        // Ops cannot send money; strip it defensively even though we hide the fields.
        total_value_inr: canEnterMoney ? (f.total_value_inr === '' ? null : Number(f.total_value_inr)) : null,
        included_visits: f.included_visits === '' ? null : Number(f.included_visits),
        per_visit_fee_inr: canEnterMoney ? (f.per_visit_fee_inr === '' ? null : Number(f.per_visit_fee_inr)) : null,
      };
      const r = await api.post('/work_orders/manual', payload);
      const wo = r.data;
      // If Ops: don't try to activate — WO sits in 'values pending' for Owner/Accounts.
      if (!canEnterMoney) {
        toast.success('Work order saved. Owner or Accounts will complete values before activation.');
        nav(`/work-orders/${wo.id}/verify`);
        return;
      }
      try {
        const act = await api.post(`/work_orders/${wo.id}/activate`, { project_name: f.project_name, assigned_user_ids: [] });
        toast.success(`Project activated: ${act.data.code}`);
        nav(`/projects/${act.data.id}`);
      } catch {
        toast.success('Work order saved. Owner will activate.');
        nav(`/work-orders/${wo.id}/verify`);
      }
    } catch (e) { toast.error(parseErr(e)); }
  };

  const goNext = () => setStep(s => Math.min(STEPS.length - 1, s + 1));
  const goBack = () => setStep(s => Math.max(0, s - 1));

  return (
    <AppShell breadcrumbs={[{ label: 'Work Orders', to: '/work-orders' }, { label: 'Add manually' }]}>
      <div className="p-6 max-w-[820px] mx-auto fade-up" data-testid="wo-manual-page">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="font-heading text-2xl font-semibold">Add Work Order</h1>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))]">Step {step + 1} of {STEPS.length}: <b>{STEPS[step]}</b></p>
          </div>
          <div className="text-[12.5px] font-mono text-[hsl(var(--foreground-muted))]">Total: <b className={totalPct === 100 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--warning))]'} data-testid="wo-manual-total-pct">{totalPct}%</b></div>
        </div>

        <div className="card p-5 space-y-3">
          {!canSeeMoney && (
            <div className="rounded-md border p-2 flex items-center gap-2 text-[12px] text-[hsl(var(--foreground-muted))]" style={{ borderColor: 'hsl(var(--border))' }} data-testid="wo-manual-ops-banner">
              <ShieldCheck size={13} className="text-[hsl(var(--success))]" />
              You&apos;re entering structural fields only. Owner or Accounts will complete monetary values before activation. You are not blocked.
            </div>
          )}
          {stepMap[step] === 0 && <Field label="Project name"><Input value={f.project_name} onChange={e => setF({ ...f, project_name: e.target.value })} data-testid="wo-manual-name" placeholder="e.g. Amber Group HQ Wayfinding" /></Field>}
          {stepMap[step] === 0 && <Field label="Client name"><Input value={f.client_name} onChange={e => setF({ ...f, client_name: e.target.value })} data-testid="wo-manual-client" placeholder="e.g. Amber Group" /></Field>}
          {stepMap[step] === 1 && <Field label="Client's WO / PO reference (optional)"><Input value={f.client_wo_reference} onChange={e => setF({ ...f, client_wo_reference: e.target.value })} data-testid="wo-manual-ref" placeholder="e.g. AMB/2025/WO-018" /></Field>}
          {stepMap[step] === 2 && (<><Field label="Address / area"><Input value={f.location_address} onChange={e => setF({ ...f, location_address: e.target.value })} /></Field>
            <Field label="City"><Input value={f.location_city} onChange={e => setF({ ...f, location_city: e.target.value })} /></Field>
            <Field label="State"><Input value={f.location_state} onChange={e => setF({ ...f, location_state: e.target.value })} /></Field></>)}
          {stepMap[step] === 3 && <Field label="Billing name (client's legal billing entity) — visible only to Owner/Accounts/Commercials">
            <Input value={f.billing_name} onChange={e => setF({ ...f, billing_name: e.target.value })} data-testid="wo-manual-billing" placeholder="e.g. Amber Group Holdings Pvt Ltd" />
          </Field>}
          {stepMap[step] === 4 && <Field label="Stage plan">
            <select className="h-9 w-full border rounded-md px-2" style={{ borderColor: 'hsl(var(--border))' }} value={f.stage_plan} onChange={e => setF({ ...f, stage_plan: e.target.value })} data-testid="wo-manual-stageplan">
              <option value="standard">Standard (6 stages)</option>
              <option value="standard_with_stage7">Standard + Stage 7 (Construction Admin)</option>
              <option value="custom">Custom stage plan</option>
            </select>
          </Field>}
          {stepMap[step] === 5 && <div className="space-y-2" data-testid="wo-manual-milestones">
            {f.milestones.map((m, i) => (
              <div key={i} className="grid grid-cols-[1fr_90px_1fr_auto] gap-2 items-center">
                <Input placeholder="Milestone name" value={m.name} onChange={e => setMs(i, 'name', e.target.value)} />
                <Input type="number" step="0.01" value={m.percentage} onChange={e => setMs(i, 'percentage', e.target.value)} data-testid={`wo-manual-ms-pct-${i}`} />
                <Input placeholder="Trigger" value={m.trigger} onChange={e => setMs(i, 'trigger', e.target.value)} />
                <Button size="sm" variant="ghost" onClick={() => rmMs(i)}><Trash2 size={13} /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addMs}><PlusCircle size={13} className="mr-1" />Add milestone</Button>
            <div className={`text-[12.5px] mt-2 ${totalPct === 100 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--warning))]'}`}>Running total: <b>{totalPct}%</b> {totalPct !== 100 && '— must be exactly 100% before you can save.'}</div>
          </div>}
          {stepMap[step] === 6 && canEnterMoney && <Field label="Total value (₹ — optional, will compute per-milestone amounts)">
            <Input type="number" value={f.total_value_inr} onChange={e => setF({ ...f, total_value_inr: e.target.value })} data-testid="wo-manual-total" />
          </Field>}
          {stepMap[step] === 7 && (<>
            <Field label="Included visits (blank = not defined, -1 = unlimited)"><Input type="number" value={f.included_visits} onChange={e => setF({ ...f, included_visits: e.target.value })} /></Field>
            {canEnterMoney && <Field label="Per-visit fee (₹)"><Input type="number" value={f.per_visit_fee_inr} onChange={e => setF({ ...f, per_visit_fee_inr: e.target.value })} /></Field>}
            <Field label="Billing mode">
              <select className="h-9 w-full border rounded-md px-2" style={{ borderColor: 'hsl(var(--border))' }} value={f.visit_billing_mode} onChange={e => setF({ ...f, visit_billing_mode: e.target.value })}>
                <option value="included_up_to_quota">Included up to quota</option>
                <option value="separately_billable">Separately billable</option>
                <option value="not_defined">Not defined</option>
              </select>
            </Field>
          </>)}
          {stepMap[step] === 8 && (
            <div className="space-y-2 text-[13px]">
              <div><b>Project:</b> {f.project_name}</div>
              <div><b>Client:</b> {f.client_name}{canSeeMoney && <> · <b>Billing name:</b> {f.billing_name || '—'}</>}</div>
              <div><b>Location:</b> {[f.location_address, f.location_city, f.location_state].filter(Boolean).join(', ') || '—'}</div>
              <div><b>Stage plan:</b> {f.stage_plan}</div>
              <div><b>Milestones:</b> {f.milestones.map(m => `${m.name} ${m.percentage}%`).join(' · ')}</div>
              {canSeeMoney && <div><b>Total value:</b> {f.total_value_inr ? `₹ ${Number(f.total_value_inr).toLocaleString('en-IN')}` : '—'}</div>}
              <div><b>Visits:</b> {f.included_visits === '' ? 'not defined' : f.included_visits}{canSeeMoney && <> · fee ₹{f.per_visit_fee_inr || '—'}</>} · {f.visit_billing_mode}</div>
              {!canSeeMoney && <div className="text-[12px] italic text-[hsl(var(--foreground-muted))]">Monetary values will be entered by Owner or Accounts.</div>}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-4">
          <Button variant="outline" onClick={goBack} disabled={step === 0} data-testid="wo-manual-back"><ChevronLeft size={14} className="mr-1" />Back</Button>
          {step < STEPS.length - 1 ? (
            <Button onClick={goNext} data-testid="wo-manual-next">Next <ChevronRight size={14} className="ml-1" /></Button>
          ) : (
            <Button onClick={submit} disabled={!canSave} data-testid="wo-manual-save"><CheckCircle2 size={14} className="mr-1" />Save & activate</Button>
          )}
        </div>
      </div>
    </AppShell>
  );
}

const Field = ({ label, children }) => (
  <label className="block"><span className="text-[12px] text-[hsl(var(--foreground-muted))]">{label}</span><div className="mt-1">{children}</div></label>
);
